import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import { styled, Theme } from '@mui/material/styles';
import { Typography, Button, Box, CircularProgress } from '@mui/material';
import { Device, Call } from '@twilio/voice-sdk';
import useHeight from './hooks/useHeight/useHeight';
import { useAppState } from './state';
import VoiceContactList from './components/VoiceComponents/VoiceContactList';
import VoiceKeypad from './components/VoiceComponents/VoiceKeypad';
import { apiGetPhoneNumber, apiGetVoiceToken } from './utils/api';
import { capitalize, formatNumber } from './utils';
import AvataraModal from './components/AvataraModal/AvataraModal';

const Container = styled('div')({
  display: 'grid',
  gridTemplateRows: '1fr auto',
});

const useStyles = makeStyles((theme: Theme) => ({
  background: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgb(40, 42, 43)',
    height: '100%',
  },
  boxContainer: {
    flex: '1',
    position: 'relative',
  },
  innerContainer: {
    display: 'flex',
    maxWidth: '570px',
    height: 'auto',
    borderRadius: '8px',
    boxShadow: '0px 2px 4px 0px rgba(40, 42, 43, 0.3)',
    overflow: 'hidden',
    position: 'relative',
    margin: 'auto',
    background: '#fff',
    minHeight: '472px',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      height: 'auto',
      width: 'calc(100% - 40px)',
      margin: 'auto',
      maxWidth: '400px',
    },
  },
  content: {
    background: 'white',
    width: '100%',
    flex: 1,
    [theme.breakpoints.down('sm')]: {},
  },
  headerRow: {
    display: 'flex',
  },
  heading: {
    fontSize: '16px',
    width: '50%',
    fontWeight: 600,
    textTransform: 'uppercase',
    cursor: 'pointer',
    textAlign: 'center',
    padding: '1em',
    borderBottom: 'solid 1px rgb(219 221 221)',
    '&:first-child': {
      borderRight: 'solid 1px rgb(219 221 221)',
    },
    '&:hover': {
      background: '#1c567b',
    },
  },
  headingBody1: {
    transition: 'all .3s',
    background: '#047ac5',
    color: '#fff',
    '&.selected': {
      background: '#fff',
      color: '#000',
    },
  },
  contentBody: {
    padding: '1em',
    height: '420px',
    overflow: 'auto',
  },
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
    marginTop: '12px',
  },
  userList: {
    display: 'block',
  },
  listItemRoot: {
    cursor: 'auto',
    '&:hover': {
      background: '#fff',
    },
  },
  disconnectBtn: {
    padding: '1em 2em',
  },
  calleePresentContainer: {
    width: '100%',
    position: 'relative',
    background: '#fff',
    minHeight: '472px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    padding: theme.spacing(3),
  },
  calleePresentHeading: {
    marginTop: theme.spacing(8),
  },
  calleePresentCallBtn: {
    marginTop: theme.spacing(3),
    padding: '1em 2em',
  },
  connectedContainer: {
    padding: theme.spacing(2),
  },
  connectedCtaContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: '24px',
  },
  connectedCta: {
    width: 'calc(50% - 16px)',
    padding: '1em',
    maxWidth: '200px',
  },
  connectedKeypadContainer: {
    visibility: 'hidden',
  },
  keypadShown: {
    visibility: 'visible',
  },
}));

const VoiceApp = () => {
  const height = useHeight();
  const classes = useStyles();
  const { username, domain, userToCall } = useAppState();
  const [ownNumber, setOwnNumber] = useState<number | null>(null);
  const [device, setDevice] = useState<Device>(null!);
  const [isManualTab, setIsManualTab] = useState<boolean>(false);
  const [activeCall, setActiveCall] = useState<Call | null>(null!);
  const [incomingCall, setIncomingCall] = useState<Call | null>(null!);
  const [deviceStatus, setDeviceStatus] = useState<Device.EventName | null>(null);
  const [callStatus, setCallStatus] = useState<Call.State | null>(null);
  const [calleeNumber, setCalleeNumber] = useState<number>(null!);
  const [showConnectedKeypad, setShowConnectedKeypad] = useState(false);
  const [isCalling, setIsCalling] = useState(false);

  useEffect(() => {
    // @ts-ignore
    window.isUserIdle = function() {
      return deviceStatus === Device.EventName.Registered && !activeCall;
    };
  }, [deviceStatus, activeCall]);

  useEffect(() => {
    // get callee number if userToCall is present
    if (userToCall) {
      apiGetPhoneNumber(userToCall, domain)
        .then(data => {
          setCalleeNumber(data.data[0].PhoneNumber);
        })
        .catch(e => console.log(e));
    }
  }, [userToCall, domain]);

  const setupDevice = useCallback(
    async (token: string) => {
      const options: Device.Options = {
        enableImprovedSignalingErrorPrecision: true,
      };
      const twilioDevice = new Device(token, options);

      twilioDevice.on(Device.EventName.Incoming, (call: Call) => {
        setDeviceStatus(Device.EventName.Incoming);
        setIncomingCall(call);

        call.on('accept', () => {
          setCallStatus(call.status());
          setActiveCall(call);
          setIncomingCall(null);
        });

        call.on('disconnect', () => {
          setCallStatus(call.status());
          setActiveCall(null);
          setIncomingCall(null);
        });
      });

      twilioDevice.on(Device.EventName.Registered, () => {
        setDeviceStatus(Device.EventName.Registered);
      });
      twilioDevice.on(Device.EventName.Unregistered, () => {
        setDeviceStatus(Device.EventName.Unregistered);
      });
      twilioDevice.on(Device.EventName.Error, error => {
        console.error(error);
        setDeviceStatus(Device.EventName.Error);
      });
      twilioDevice.on(Device.EventName.TokenWillExpire, async () => {
        const newToken = await apiGetVoiceToken(username, domain);
        twilioDevice.updateToken(newToken!);
      });
      await twilioDevice.register();
      setDevice(twilioDevice);
      // @ts-ignore
      window.device = twilioDevice;
    },
    [domain, username]
  );

  useEffect(() => {
    return () => {
      if (device) {
        device.destroy();
      }
    };
  }, []);

  useEffect(() => {
    const initSetup = async () => {
      const phonePromise = apiGetPhoneNumber(username, domain);
      const voiceTokenPromise = apiGetVoiceToken(username, domain);
      const [phoneData, token] = await Promise.all([phonePromise, voiceTokenPromise]);
      const phNumber = phoneData?.data?.[0]?.PhoneNumber;
      setOwnNumber(phNumber);
      setupDevice(token);
    };
    if (username && domain && !device) {
      initSetup();
    }
  }, [username, domain, device, setupDevice]);

  const handleCall = async (toNumber: string | number) => {
    console.log('[handleCall]');
    if (isCalling || !device) return;

    setIsCalling(true);
    const call = await device.connect({
      params: {
        To: formatNumber(toNumber),
        callerId: ownNumber && String(ownNumber).length === 10 ? '+1' + ownNumber : '+' + ownNumber,
      },
    });

    // Attach call listeners
    // attachCallListeners(call);
    call.on('accept', () => {
      setCallStatus(call.status());
      setActiveCall(call);
      setIsCalling(false);
    });

    call.on('disconnect', () => {
      setCallStatus(call.status());
      setActiveCall(null);
      setIsCalling(false);
    });

    call.on('error', error => {
      setCallStatus(call.status());
      setActiveCall(null);
      setIsCalling(false);
    });

    // setActiveCall(call);
    // console.log('[handleCall] call status: ', call.status());
    // setCallStatus(call.status());
  };

  const handleHangUpClick = () => {
    if (!activeCall) return;
    activeCall.disconnect();
    setActiveCall(null);
    setCallStatus(Call.State.Closed);
  };

  return (
    <Container style={{ height }}>
      <div className={classes.background}>
        <div className={classes.boxContainer}>
          <div className={classes.innerContainer}>
            {(!device || deviceStatus === Device.EventName.Registering) && (
              <Box className="loader">
                <CircularProgress />
              </Box>
            )}
            {deviceStatus === Device.EventName.Registered && (
              <>
                {!activeCall && !userToCall && (
                  <div className={classes.content}>
                    <div className={classes.headerRow}>
                      <Typography
                        className={`${classes.heading} ${isManualTab ? '' : 'selected'}`}
                        variant="h2"
                        onClick={() => setIsManualTab(false)}
                        classes={{ h2: classes.headingBody1 }}
                      >
                        Select Contact
                      </Typography>
                      <Typography
                        className={`${classes.heading} ${isManualTab ? 'selected' : ''}`}
                        variant="h2"
                        onClick={() => setIsManualTab(true)}
                        classes={{ h2: classes.headingBody1 }}
                      >
                        Manual Dial
                      </Typography>
                    </div>
                    <div className={classes.contentBody}>
                      {/* Select Contact */}
                      {!isManualTab && <VoiceContactList handleCall={handleCall} />}
                      {/* Manual */}
                      {isManualTab && <VoiceKeypad handleCall={handleCall} />}
                    </div>
                  </div>
                )}
                {!activeCall && !!userToCall && calleeNumber && (
                  <div className={classes.calleePresentContainer}>
                    <Typography className={classes.calleePresentHeading} variant="h5">
                      {capitalize(userToCall)} ({formatNumber(calleeNumber)})
                    </Typography>
                    <Button
                      className={classes.calleePresentCallBtn}
                      color="primary"
                      variant="contained"
                      onClick={() => handleCall(calleeNumber)}
                      disabled={isCalling}
                    >
                      Call
                    </Button>
                  </div>
                )}
                {activeCall && (
                  <div className={classes.connectedContainer}>
                    <div
                      className={clsx(classes.connectedKeypadContainer, {
                        [classes.keypadShown]: showConnectedKeypad,
                      })}
                    >
                      <VoiceKeypad handleCall={handleCall} activeCall={activeCall} />
                    </div>
                    <div className={classes.connectedCtaContainer}>
                      <Button
                        className={classes.connectedCta}
                        color="primary"
                        variant="contained"
                        onClick={handleHangUpClick}
                      >
                        Hang up
                      </Button>
                      <Button
                        className={classes.connectedCta}
                        color="primary"
                        variant="contained"
                        onClick={() => setShowConnectedKeypad(!showConnectedKeypad)}
                      >
                        {showConnectedKeypad ? 'Hide' : 'Show'} Keypad
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
            {deviceStatus === Device.EventName.Error && (
              <Typography>Something went wrong. Please refresh the page or try again later.</Typography>
            )}
            <AvataraModal
              open={!!incomingCall}
              handleClose={() => {
                console.log('closing incoming call modal');
              }}
            >
              <Typography>Getting a call from {incomingCall?.parameters.From}</Typography>
              <Button
                onClick={() => {
                  incomingCall?.accept();
                  setDeviceStatus(Device.EventName.Registered);
                }}
              >
                Accept
              </Button>
              <Button
                onClick={() => {
                  incomingCall?.reject();
                  setIncomingCall(null);
                  setDeviceStatus(Device.EventName.Registered);
                }}
              >
                Reject
              </Button>
            </AvataraModal>
          </div>
          {/* <Box sx={{ position: 'fixed', top: 0, left: 0, background: '#fff', p: 2 }}>
            <Typography sx={{ fontWeight: 700 }}>Device/Call details</Typography>
            <Typography>Device Status: {deviceStatus}</Typography>
            <Typography>Call Status: {callStatus}</Typography>
            <Typography>Own Number: {ownNumber}</Typography>
            <Typography>Active Call: {activeCall?.parameters.To}</Typography>
            <Typography>Active Call From: {activeCall?.parameters.From}</Typography>
            <Typography>Incoming Call: {JSON.stringify(incomingCall?.parameters, null, 2)}</Typography>
            <Typography>isCalling?: {isCalling ? 'true' : 'false'}</Typography>
            {JSON.stringify(activeCall?.parameters, null, 2)}
          </Box> */}
        </div>
      </div>
    </Container>
  );
};

export default VoiceApp;
